import React, { useRef } from 'react';
import { formatCurrency } from '@/lib/payrollCalc';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const AbdLogo = ({ height = 36 }) => (
  <svg
    aria-hidden="true"
    fill="none"
    viewBox="18 8 48 42"
    xmlns="http://www.w3.org/2000/svg"
    style={{ height, width: 'auto' }}
  >
    <path
      clipRule="evenodd"
      d="M22.533 38.094h24.408c.313 0 .04-.234-.117-.586l-1.835-3.4a15.982 15.982 0 0 1-.742-1.406H32.375v-.117l3.436-6.251c.703-1.212 1.68-3.165 2.343-4.298l2.343-4.298c.196-.351 1.016-1.992 1.211-2.149.04.196.117.313.235.47l.742 1.328c.859 1.445 1.718 3.047 2.577 4.454 1.015 1.719 2.07 3.672 3.085 5.352l2.07 3.595c.156.273.312.586.508.898.351.586.664 1.172 1.054 1.798.352.586.664 1.172 1.015 1.797l4.14 7.189c.117.234.976 1.602 1.015 1.797H18.667v5.353h46.121c.118 0 .157-.04.157-.156v-4.845c0-.196-.47-.938-.586-1.133l-3.163-5.51c-.118-.195-.196-.35-.313-.546L54.83 26.92c-.117-.196-.195-.352-.313-.547l-6.053-10.471c-.117-.196-.195-.352-.312-.547L45.3 10.393c-.156-.235-.586-.938-.625-1.172h-5.819c-.039.195-.351.664-.468.898-.196.313-.313.586-.508.899l-1.015 1.797c-.196.313-.313.586-.508.899a6.875 6.875 0 0 1-.547.899l-2.07 3.633c-.351.625-.663 1.211-1.015 1.797l-6.248 10.862c-.352.625-.664 1.211-1.016 1.797l-2.616 4.532c-.274.508-.586.82-.274.82"
      fill="#B5171B"
      fillRule="evenodd"
    />
  </svg>
);

const getNonZeroLineItems = (items) =>
  items.filter(([, amount]) => Number(amount) > 0);

export function PaySlipModal({ open, onOpenChange, employee, payData, month, year }) {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Pay Slip - ${employee?.firstName} ${employee?.lastName} - ${MONTHS[month - 1]} ${year}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700&family=Inter:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #0f172a; background: #fff; }
            h1, h2, h3 { font-family: 'Manrope', sans-serif; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0047AB; padding-bottom: 20px; margin-bottom: 24px; }
            .logo { font-size: 28px; font-weight: 700; }
            .logo span { color: #0047AB; }
            .meta { text-align: right; font-size: 12px; color: #64748b; }
            .meta strong { color: #0f172a; display: block; font-size: 14px; }
            .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #64748b; font-weight: 600; margin: 20px 0 10px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 32px; margin-bottom: 20px; }
            .info-row { display: flex; justify-content: space-between; font-size: 13px; padding: 4px 0; }
            .info-row .label { color: #64748b; }
            .info-row .value { font-weight: 500; }
            .table { width: 100%; border-collapse: collapse; margin: 12px 0; }
            .table th, .table td { padding: 10px 12px; text-align: left; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
            .table th { background: #f8fafc; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #64748b; font-weight: 600; }
            .table td.amount { text-align: right; font-family: 'JetBrains Mono', monospace; }
            .table th.amount { text-align: right; }
            .table tr.total { border-top: 2px solid #0047AB; }
            .table tr.total td { font-weight: 700; font-size: 14px; padding-top: 12px; }
            .table tr.total td.amount { color: #0047AB; }
            .net-pay { background: #0047AB; color: white; padding: 16px 20px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; margin-top: 24px; }
            .net-pay .label { font-size: 14px; font-weight: 600; }
            .net-pay .amount { font-size: 24px; font-weight: 700; font-family: 'JetBrains Mono', monospace; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
            .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
            .deduction { color: #ef4444; }
            @media print { body { padding: 20px; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  if (!employee || !payData) return null;

  // Payslip-only: offset LOP with bonus days, then exclude bonus from paid days
  const rawLopDays = Math.max(Number(payData.lopDays) || 0, 0);
  const rawBonusDays = Math.max(Number(payData.bonusDays) || 0, 0);
  const lopOffset = Math.min(rawLopDays, rawBonusDays);
  const slipLopDays = rawLopDays - lopOffset;
  const slipBonusDays = rawBonusDays - lopOffset;
  const daysInMonth = Math.max(Number(payData.daysInMonth) || 0, 0);
  const perDayRate =
    Number(payData.perDayRate) ||
    (daysInMonth > 0 ? (Number(payData.gross) || 0) / daysInMonth : 0);
  const fixedMonthlyBonus = Math.max(Number(payData.fixedMonthlyBonus) || 0, 0);
  const slipMonthlyBonus = fixedMonthlyBonus + Math.round(perDayRate * slipBonusDays);
  const slipLopDeduction = Math.round(perDayRate * slipLopDays);
  const slipPaidDays =
    daysInMonth > 0
      ? daysInMonth - slipLopDays
      : Math.max((Number(payData.paidDays) || 0) - rawBonusDays, 0);

  const arrearsAmount = Math.max(Number(payData?.arrearsAmount) || 0, 0);

  const earningItems = getNonZeroLineItems([
    ['Basic Salary', payData.basic],
    ['HRA', payData.hra],
    ['Special Allowance', payData.specialAllowance],
    ['Conveyance', payData.conveyance],
    ...(arrearsAmount > 0 ? [['Arrears', arrearsAmount]] : []),
    ...(slipMonthlyBonus > 0
      ? [[`Extra Working Days (${slipBonusDays} days)`, slipMonthlyBonus]]
      : []),
  ]);

  const slipOther = Math.max(Number(payData.other) || 0, 0);

  const deductionItems = getNonZeroLineItems([
    ['Provident Fund (PF)', payData.pf],
    ['ESI', payData.esi],
    ['Professional Tax', payData.pt],
    ['TDS', payData.tds],
    ['Advance Deduction', payData.advanceDeduction],
    ...(slipLopDeduction > 0
      ? [[`Leave Deduction (LOP) (${slipLopDays} days)`, slipLopDeduction]]
      : []),
    ...(slipOther > 0 ? [['Other', slipOther]] : []),
  ]);

  const grossEarnings = (payData.gross != null
    ? Number(payData.gross) + (slipMonthlyBonus > 0 ? slipMonthlyBonus : 0)
    : Number(payData.effectiveGross ?? 0) + Number(payData.lopDeduction ?? 0) - (Number(payData.monthlyBonus) || 0) + slipMonthlyBonus)
    + arrearsAmount;

  const totalDeductions =
    Number(payData.totalDeductions ?? 0) +
    (slipLopDeduction > 0 ? slipLopDeduction : 0) +
    slipOther;

  const originalLopDeduction = Number(payData.lopDeduction) || 0;
  const originalMonthlyBonus = Number(payData.monthlyBonus) || 0;
  const slipNetPay = Math.round(
    (Number(payData.netPay) || 0) +
      (originalLopDeduction - slipLopDeduction) +
      (slipMonthlyBonus - originalMonthlyBonus) -
      slipOther
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">
          Pay Slip - {employee.firstName} {employee.lastName} - {MONTHS[month - 1]} {year}
        </DialogTitle>

        <div ref={printRef}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0047AB', paddingBottom: '16px', marginBottom: '20px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AbdLogo />
                <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '24px', fontWeight: 700, color: '#B5171B' }}>
                  ABD
                </h1>
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Pay Slip for {MONTHS[month - 1]} {year}</p>
            </div>
            <div style={{ textAlign: 'right', paddingRight: '32px' }}>
              <p style={{ fontSize: '12px', color: '#64748b' }}>Employee ID</p>
              <p style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>{payData.employeeId}</p>
            </div>
          </div>

          {/* Employee Info */}
          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, marginBottom: '8px' }}>Employee Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 32px', marginBottom: '20px', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Name</span>
              <span style={{ fontWeight: 500 }}>{employee.firstName} {employee.lastName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Department</span>
              <span style={{ fontWeight: 500 }}>{employee.department}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Designation</span>
              <span style={{ fontWeight: 500 }}>{employee.designation}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>PAN</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee.panNumber || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>UAN</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee.uanNumber || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: '#64748b' }}>Bank Account</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee.bankAccount || '-'}</span>
            </div>
          </div>

          {/* Earnings & Deductions side by side */}
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '8px' }}>
              <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, margin: 0 }}>Earnings</p>
              <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, margin: 0 }}>Deductions</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '8px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600 }}>
                <span>Component</span>
                <span style={{ textAlign: 'right' }}>Amount</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '8px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600 }}>
                <span>Component</span>
                <span style={{ textAlign: 'right' }}>Amount</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '6px 8px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
                  <span>Paid Days</span>
                  <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{slipPaidDays}</span>
                </div>
                {earningItems.map(([label, val]) => (
                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '6px 8px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
                    <span>{label}</span>
                    <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(val)}</span>
                  </div>
                ))}
              </div>
              <div>
                {deductionItems.map(([label, val]) => (
                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '6px 8px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
                    <span>{label}</span>
                    <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#ef4444' }}>{formatCurrency(Number(val) || 0)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '10px 8px', fontSize: '14px', fontWeight: 700, borderTop: '2px solid #0047AB' }}>
                <span>Gross Earnings</span>
                <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#0047AB' }}>{formatCurrency(grossEarnings)}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '10px 8px', fontSize: '14px', fontWeight: 700, borderTop: '2px solid #ef4444' }}>
                <span>Total Deductions</span>
                <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#ef4444' }}>{formatCurrency(totalDeductions)}</span>
              </div>
            </div>
          </div>

          {/* Net Pay */}
          <div style={{ background: '#0047AB', color: 'white', padding: '16px 20px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>NET PAY</span>
            <span style={{ fontSize: '24px', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(slipNetPay)}</span>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '40px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#94a3b8', textAlign: 'center' }}>
            This is a computer-generated pay slip and does not require a signature.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="print-payslip-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Pay Slip
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
